import {neverland as $, html, useState} from 'neverland';
import { signIn, hasIdToken } from '../lib/cognito.js';
import { resolveLoginDecision } from '../lib/clusterAccess.js';
// TRANSITION NOTICE (temporary): login-migration guidance. To remove after the
// transition period, delete this import, the guarded `SHOW_TRANSITION_NOTICE`
// render calls below, the TransitionLoginNotice.js file, its CSS block, and the
// two guide PDFs. See TransitionLoginNotice.js for full removal steps.
import { SHOW_TRANSITION_NOTICE, TransitionNoticeOrg, TransitionNoticeCredentials, TransitionNoticeFallback } from './TransitionLoginNotice.js';

const LoginPage = $(function(auth, setAuth, theme) {

  const [step, setStep] = useState('org'); // 'org' | 'credentials'
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [orgData, setOrgData] = useState(null);

  function getLogo() {
    if (theme !== null) {
      if (theme.hasOwnProperty('logo') && theme.logo !== '') {
        return '/static/img/logos-large/' + theme.logo;
      }
    }
    return '/static/img/transparent-placeholder.png';
  }

  async function lookupOrg() {
    setError('');
    setLoading(true);

    const orgCode = document.querySelector('#login-orgcode').value.trim();

    if (!orgCode) {
      setError('Please enter your school username.');
      setLoading(false);
      return;
    }

    try {
      const clientGroups = await auth.db.collection('ClientGroups').find({ username: orgCode });

      if (clientGroups.length === 0) {
        setError('School not found. Check your username and try again.');
        setLoading(false);
        return;
      }

      setOrgData(clientGroups[0]);
      setStep('credentials');
    } catch (err) {
      setError('Failed to look up school. Please try again.');
    }

    setLoading(false);
  }

  async function logIn() {
    setError('');
    setLoading(true);

    const email = document.querySelector('#login-email').value.trim().toLowerCase();
    const password = document.querySelector('#login-password').value;

    if (!email || !password) {
      setError('Please enter your email and password.');
      setLoading(false);
      return;
    }

    // Clear any stale persisted auth/refresh token from a previous user BEFORE
    // signing in; a successful signIn persists this user's own refresh token.
    localStorage.removeItem('reportingAuth');
    localStorage.removeItem('reportingRefreshToken');

    try {
      await signIn(email, password);
      await completeLogin(email);
    } catch (err) {
      if (err.type === 'MFA_SETUP') {
        // First login — user needs to enrol their authenticator app.
        setAuth(prev => ({ ...prev, mfaPage: 'MFASetup', mfaChallenge: err, userEmail: email, userPassword: password, userData: orgData }));
      } else if (err.type === 'MFA_REQUIRED') {
        setAuth(prev => ({ ...prev, mfaPage: 'MFAVerify', mfaChallenge: err, userEmail: email, userPassword: password, userData: orgData }));
      } else if (err.type === 'NEW_PASSWORD_REQUIRED') {
        setAuth(prev => ({ ...prev, mfaPage: 'NewPassword', mfaChallenge: err, userEmail: email, userPassword: password, userData: orgData }));
      } else {
        setError('Invalid email or password.');
      }
    }

    setLoading(false);
  }

  async function completeLogin(email) {
    // Reached only when signIn resolves with NO MFA / new-password challenge.
    // There is no cognito token on this path, so cognito groups are
    // unavailable (groups: []). Admins are not expected here — they always go
    // through the MFA path where groups ARE extracted — so a missing-groups
    // admin falling through to 'register' is acceptable (and never a silent
    // login). The gate is the single source of truth for the pending/removed
    // split; orgData is the ClientGroup doc (userData with users[]/contacts[]).
    const decision = await resolveLoginDecision({ email, groups: [], userData: orgData, db: auth.db, isTokenReady: hasIdToken });

    if (decision.action === 'revoked') {
      setError(decision.message);
      return;
    } else if (decision.action === 'register') {
      setAuth(prev => ({
        ...prev,
        userData: orgData,
        userEmail: email,
        mfaPage: 'ConfirmOrgAccess'
      }));
      return;
    } else if (decision.action === 'pending') {
      setAuth(prev => ({
        ...prev,
        userData: orgData,
        userEmail: email,
        mfaPage: 'PendingApproval'
      }));
      return;
    }

    setAuth(prev => ({
      ...prev,
      userData: orgData,
      userEmail: email,
      mfaPage: null,
      mfaVerified: true,
      clusterAccess: decision.clusterAccess
    }));
  }

  function handleOrgKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      lookupOrg();
    }
  }

  function handleLoginKeyPress(event) {
    if (event.keyCode === 13) {
      event.preventDefault();
      logIn();
    }
  }

  function goToRegister() {
    setAuth(prev => ({ ...prev, mfaPage: 'Register', userData: orgData }));
  }

  function goToForgotPassword() {
    setAuth(prev => ({ ...prev, mfaPage: 'ForgotPassword' }));
  }

  function goBackToOrg() {
    setStep('org');
    setOrgData(null);
    setError('');
  }

  function InfoTip(text) {
    return html`<span class="info-tip" title="${text}"><i class="fal fa-info-circle"></i></span>`;
  }

  return html`
    <div class="centre-box login">
      <img src="${getLogo()}" class="squiggle logo-large" />

      <div id="login">
        ${step === 'org' ? html`
          ${/* TRANSITION NOTICE (temporary): top callout on the school-username page */ ''}
          ${SHOW_TRANSITION_NOTICE ? TransitionNoticeOrg() : ''}
          <span class="loginlabel">SCHOOL USERNAME ${InfoTip('The unique username assigned to your school / organisation. Ask your school administrator if unsure. e.g. \"SpringfieldPrimarySchool\"')}</span></span>
          <input type="text" id="login-orgcode" class="login-field" placeholder="Enter your school / organisation username" onkeyup=${handleOrgKeyPress}><br />
          <button id="login-submit" onclick=${lookupOrg} disabled=${loading}>
            ${loading ? 'Looking up...' : html`<i class="fal fa-arrow-right"></i>&ensp;CONTINUE`}
          </button>
        ` : html`
          <p style="margin-bottom: 12px; font-size: 1em; opacity: 0.8;">
            Logging in to <strong>${orgData.name}</strong>
          </p>

          ${/* TRANSITION NOTICE (temporary): top callout on the email/password page */ ''}
          ${SHOW_TRANSITION_NOTICE ? TransitionNoticeCredentials(goToRegister) : ''}

          <span class="loginlabel">EMAIL ${InfoTip('Your work email address that was used to register. e.g. \"j.smith@greenvalley.edu.au\"')}</span>
          <input type="email" id="login-email" class="login-field" placeholder="you@yourschool.edu.au"><br />
          <span class="loginlabel">PASSWORD ${InfoTip('The password you set during registration. Must be at least 8 characters.')}</span>
          <input type="password" id="login-password" class="login-field" onkeyup=${handleLoginKeyPress}><br />
          <button id="login-submit" onclick=${logIn} disabled=${loading}>
            ${loading ? 'Signing in...' : html`<i class="fal fa-sign-in-alt"></i>&ensp;LOGIN`}
          </button>

          <button class="back-link" onclick=${goToRegister}>
            First time? Register here
          </button>

          <button class="back-link" onclick=${goToForgotPassword}>
            Forgot password?
          </button>

          <button class="back-link" onclick=${goBackToOrg}>
            &larr; Different school
          </button>
        `}

        ${error ? html`
          <div class="error">
            <strong>ERROR</strong>
            <p>${error}</p>
          </div>
        ` : ''}

        ${/* TRANSITION NOTICE (temporary): secondary fallback link at the bottom of both steps */ ''}
        ${SHOW_TRANSITION_NOTICE ? TransitionNoticeFallback() : ''}
      </div>
    </div>
  `;

});

export default LoginPage;
